import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useHubTab } from '../components/syndication/HubTabs';
import SyndicationLedger from './syndication/SyndicationLedger';
import SyndicatorFeeLedger from './syndication/SyndicatorFeeLedger';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, DollarSign, Target, TrendingUp, ArrowDownCircle, Wallet, RefreshCw, Search, ArrowUpDown, ChevronsUpDown } from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency } from '../lib/formatters';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const dealCategory = (status) => {
  const s = (status || '').toLowerCase();
  if (s.includes('refinanc')) return 'REFINANCED';
  if (s.includes('default')) return 'DEFAULTED';
  if (s.includes('paid in full') || s.includes('settled')) return 'PAID';
  return 'ACTIVE';
};

// 11/03 (Josh: consolidate): Ledger and Fee Ledger live here as tabs; the syndicator is fixed by the URL.
const DETAIL_TABS = [{ key: 'overview', label: 'Overview' }, { key: 'ledger', label: 'Ledger' }, { key: 'fees', label: 'Fee ledger' }];

const SyndicatorDetail = () => {
  const { id } = useParams();
  const [tab, setTab] = useHubTab(DETAIL_TABS, 'overview');
  const [expanded, setExpanded] = useState({});
  const [statusFilter, setStatusFilter] = useState('ALL');
  const navigate = useNavigate();
  const { getAuthHeaders, user } = useAuth();
  const isAdmin = user?.roles?.includes('admin');

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [withdrawAmounts, setWithdrawAmounts] = useState({});
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortKey, setSortKey] = useState('funded_date_raw');
  const [sortDir, setSortDir] = useState('desc');
  const [bulkValue, setBulkValue] = useState('');

  const headers = useMemo(() => ({
    ...getAuthHeaders(),
    'X-Tenant': 'live'
  }), [getAuthHeaders]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/syndications/syndicator/${id}/detail`, { headers });
      setData(res.data);
      // Initialize withdraw amounts to 0
      const init = {};
      (res.data.deals || []).forEach(d => { init[d.deal_id] = ''; });
      setWithdrawAmounts(init);
      setErrors({});
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load syndicator details');
    } finally {
      setLoading(false);
    }
  }, [id, headers]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Sorting
  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir(key === 'funded_date_raw' ? 'desc' : 'asc');
    }
  };

  const SortHeader = ({ label, field, className }) => (
    <TableHead
      className={`cursor-pointer select-none hover:bg-slate-100 ${className || ''}`}
      onClick={() => toggleSort(field)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {sortKey === field
          ? <ArrowUpDown className="h-3 w-3 text-blue-500" />
          : <ChevronsUpDown className="h-3 w-3 text-gray-300" />}
      </span>
    </TableHead>
  );

  // Filtered + sorted deals
  const deals = useMemo(() => {
    if (!data?.deals) return [];
    let list = data.deals;
    if (statusFilter !== 'ALL') {
      list = list.filter(d => dealCategory(d.status) === statusFilter);
    }
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      list = list.filter(d =>
        (d.merchant || '').toLowerCase().includes(q) ||
        (d.deal_name || '').toLowerCase().includes(q) ||
        (d.shortcode || '').toLowerCase().includes(q) ||
        String(d.deal_number || '').includes(q)
      );
    }
    return [...list].sort((a, b) => {
      let av = a[sortKey], bv = b[sortKey];
      if (typeof av === 'string') av = av.toLowerCase();
      if (typeof bv === 'string') bv = bv.toLowerCase();
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [data, searchTerm, sortKey, sortDir, statusFilter]);

  // Bulk controls
  const setAllToBalance = () => {
    const next = {};
    (data?.deals || []).forEach(d => {
      next[d.deal_id] = d.balance > 0 ? d.balance.toFixed(2) : '';
    });
    setWithdrawAmounts(next);
    setErrors({});
  };

  const setAllToZero = () => {
    const next = {};
    (data?.deals || []).forEach(d => { next[d.deal_id] = ''; });
    setWithdrawAmounts(next);
    setErrors({});
  };

  const setAllToValue = () => {
    const v = parseFloat(bulkValue);
    if (isNaN(v) || v < 0) { toast.error('Enter a valid positive number'); return; }
    const next = {};
    const errs = {};
    (data?.deals || []).forEach(d => {
      if (v > d.balance) {
        next[d.deal_id] = d.balance > 0 ? d.balance.toFixed(2) : '';
        if (d.balance > 0) errs[d.deal_id] = `Capped at balance ${formatCurrency(d.balance)}`;
      } else {
        next[d.deal_id] = v > 0 ? v.toFixed(2) : '';
      }
    });
    setWithdrawAmounts(next);
    setErrors(errs);
  };

  const handleAmountChange = (dealId, value) => {
    setWithdrawAmounts(prev => ({ ...prev, [dealId]: value }));
    const deal = data?.deals?.find(d => d.deal_id === dealId);
    if (!deal) return;
    const num = parseFloat(value);
    if (value && (isNaN(num) || num < 0)) {
      setErrors(prev => ({ ...prev, [dealId]: 'Invalid amount' }));
    } else if (num > deal.balance) {
      setErrors(prev => ({ ...prev, [dealId]: `Exceeds balance ${formatCurrency(deal.balance)}` }));
    } else {
      setErrors(prev => { const n = { ...prev }; delete n[dealId]; return n; });
    }
  };

  const handleWithdraw = async () => {
    // Validate all
    const items = [];
    const newErrors = {};
    for (const [dealId, val] of Object.entries(withdrawAmounts)) {
      if (!val || val === '0' || val === '0.00') continue;
      const num = parseFloat(val);
      const deal = data?.deals?.find(d => d.deal_id === dealId);
      if (isNaN(num) || num <= 0) continue;
      if (num < 0) { newErrors[dealId] = 'Cannot be negative'; continue; }
      if (deal && num > deal.balance + 0.01) { newErrors[dealId] = `Exceeds balance ${formatCurrency(deal.balance)}`; continue; }
      items.push({ deal_id: dealId, amount: num });
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(prev => ({ ...prev, ...newErrors }));
      toast.error('Fix validation errors before withdrawing');
      return;
    }
    if (items.length === 0) {
      toast.error('No withdrawal amounts entered');
      return;
    }

    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/syndications/syndicator/${id}/bulk-withdraw`,
        { items },
        { headers }
      );
      toast.success(res.data.message);
      if (res.data.errors?.length > 0) {
        res.data.errors.forEach(e => toast.error(`${e.deal_id}: ${e.error}`));
      }
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Withdrawal failed');
    } finally {
      setSubmitting(false);
    }
  };

  const withdrawTotal = useMemo(() => {
    return Object.values(withdrawAmounts).reduce((sum, v) => {
      const n = parseFloat(v);
      return sum + (isNaN(n) || n < 0 ? 0 : n);
    }, 0);
  }, [withdrawAmounts]);

  const hasErrors = Object.keys(errors).length > 0;

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-24">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
        </div>
      </Layout>
    );
  }

  if (!data) {
    return (
      <Layout>
        <div className="text-center py-24 text-gray-500">Syndicator not found.</div>
      </Layout>
    );
  }

  const _sumDeals = (k) => deals.reduce((acc, d) => acc + (Number(d[k]) || 0), 0);
  const t = {
    principal_invested: _sumDeals('advance_amount'),
    target_collection: _sumDeals('target_collection'),
    running_collection: _sumDeals('running_collection'),
    total_withdrawals: _sumDeals('total_withdrawals'),
    available_balance: _sumDeals('balance'),
    available_fees: _sumDeals('available_fees'),
  };

  return (
    <Layout>
      <div className="px-4 sm:px-0 space-y-5" data-testid="syndicator-detail-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" onClick={() => navigate('/syndication-dashboard')} data-testid="back-btn">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900" data-testid="syndicator-name">
              {data.syndicator_name}
              <span className="text-base font-normal text-gray-400 ml-2">({data.deal_count} deals)</span>
            </h1>
            <Link to={`/portal/${id}`} className="text-sm text-blue-700 underline">Open investor dashboard and reports</Link>
          </div>
        </div>

        <div className="flex flex-wrap gap-1 border-b" role="tablist">
          {DETAIL_TABS.map(t => (
            <button key={t.key} type="button" role="tab" aria-selected={t.key === tab} onClick={() => setTab(t.key)} data-testid={`syndicator-tab-${t.key}`}
                    className={`px-3 py-2 text-sm -mb-px border-b-2 ${t.key === tab ? 'border-blue-600 text-blue-700 font-semibold' : 'border-transparent text-slate-500 hover:text-slate-800'}`}>
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'ledger' && <SyndicationLedger embedded syndicatorId={id} />}
        {tab === 'fees' && <SyndicatorFeeLedger embedded syndicatorId={id} />}
        {tab === 'overview' && <>
        {/* Status filter */}
        <div className="flex flex-wrap gap-2">
          {[['ALL', 'All'], ['ACTIVE', 'Active'], ['PAID', 'Paid in Full'], ['REFINANCED', 'Refinanced'], ['DEFAULTED', 'Defaulted']].map(([k, label]) => (
            <button key={k} onClick={() => setStatusFilter(k)} data-testid={`filter-${k.toLowerCase()}`}
              className={`px-3 py-1 rounded-full text-sm border ${statusFilter === k ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
              {label}
            </button>
          ))}
        </div>

        {/* Grand total available */}
        <Card className="border-emerald-200 bg-emerald-50">
          <CardContent className="px-5 py-4 flex items-center justify-between">
            <div>
              <div className="text-xs font-medium text-emerald-700 uppercase tracking-wide">Total Available</div>
              <div className="text-3xl font-bold text-emerald-700" data-testid="grand-total-available">
                {formatCurrency((t.available_balance || 0) + (t.available_fees || 0))}
              </div>
              <div className="text-xs text-emerald-700/80 mt-0.5">
                From deals {formatCurrency(t.available_balance)} + fees {formatCurrency(t.available_fees)} · across {deals.length} deals{statusFilter !== 'ALL' ? ` (${statusFilter.toLowerCase()})` : ''}
              </div>
            </div>
            <Wallet className="h-8 w-8 text-emerald-600" />
          </CardContent>
        </Card>

        {/* Totals */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {[
            { label: 'Wire In', value: t.principal_invested, color: 'text-blue-600', icon: Wallet },
            { label: 'Target Collection', value: t.target_collection, color: 'text-slate-700', icon: Target },
            { label: 'Running Collection', value: t.running_collection, color: 'text-green-600', icon: TrendingUp },
            { label: 'Total Withdrawn', value: t.total_withdrawals, color: 'text-orange-600', icon: ArrowDownCircle },
            { label: 'Available Balance', value: t.available_balance, color: 'text-emerald-600', icon: DollarSign },
            { label: 'Available Fees', value: t.available_fees, color: 'text-purple-600', icon: DollarSign },
          ].map(c => (
            <Card key={c.label} className="border-gray-200">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1 pt-3 px-4">
                <CardTitle className="text-xs font-medium text-gray-500">{c.label}</CardTitle>
                <c.icon className={`h-3.5 w-3.5 ${c.color}`} />
              </CardHeader>
              <CardContent className="px-4 pb-3">
                <div className={`text-lg font-bold ${c.color}`} data-testid={`total-${c.label.toLowerCase().replace(/ /g, '-')}`}>
                  {formatCurrency(c.value)}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Controls row */}
        <Card>
          <CardContent className="py-3 flex flex-wrap items-center gap-3">
            {/* Search */}
            <div className="relative flex-1 min-w-[180px] max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search merchant or deal..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="pl-9 h-9"
                data-testid="deal-search"
              />
            </div>

            <div className="flex-1" />

            {/* Bulk controls */}
            {isAdmin && (
              <>
                <Button variant="outline" size="sm" onClick={setAllToBalance} data-testid="set-all-balance-btn">
                  Set All to Balance
                </Button>
                <Button variant="outline" size="sm" onClick={setAllToZero} data-testid="set-all-zero-btn">
                  Set All to 0
                </Button>
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-gray-500 whitespace-nowrap">Set All =</span>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={bulkValue}
                    onChange={e => setBulkValue(e.target.value)}
                    className="w-24 h-9"
                    placeholder="0.00"
                    data-testid="bulk-value-input"
                  />
                  <Button variant="outline" size="sm" onClick={setAllToValue} data-testid="apply-bulk-btn">
                    Apply
                  </Button>
                </div>
                <Button
                  onClick={handleWithdraw}
                  disabled={submitting || withdrawTotal <= 0 || hasErrors}
                  className="bg-green-600 hover:bg-green-700 text-white"
                  data-testid="withdraw-btn"
                >
                  {submitting ? <RefreshCw className="h-4 w-4 mr-1 animate-spin" /> : <ArrowDownCircle className="h-4 w-4 mr-1" />}
                  {submitting ? 'Processing...' : `Withdraw ${formatCurrency(withdrawTotal)}`}
                </Button>
              </>
            )}
          </CardContent>
        </Card>

        {/* Deals table */}
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table data-testid="syndicator-deals-table">
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <SortHeader label="Deal ID" field="deal_number" />
                    <SortHeader label="Merchant" field="merchant" />
                    <SortHeader label="Funded Date" field="funded_date_raw" />
                    <SortHeader label="Advance Amt" field="advance_amount" className="text-right" />
                    <SortHeader label="Target Collection" field="target_collection" className="text-right" />
                    <SortHeader label="Running Collection" field="running_collection" className="text-right" />
                    <SortHeader label="Total Withdrawals" field="total_withdrawals" className="text-right" />
                    <SortHeader label="Balance" field="balance" className="text-right" />
                    {isAdmin && <TableHead className="text-right w-36">Withdraw Amt</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {deals.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={isAdmin ? 9 : 8} className="text-center py-8 text-gray-400">
                        {searchTerm ? 'No deals match your search.' : 'No deals found.'}
                      </TableCell>
                    </TableRow>
                  ) : deals.map(d => {
                    const dimRow = d.balance <= 0;
                    const overpaid = d.running_collection > d.target_collection && d.target_collection > 0;
                    const errMsg = errors[d.deal_id];
                    const p = d.performance || {};
                    const isOpen = !!expanded[d.deal_id];
                    return [
                      <TableRow
                        key={d.deal_id}
                        className={`${dimRow ? 'opacity-50' : ''} cursor-pointer`}
                        onClick={() => setExpanded(s => ({ ...s, [d.deal_id]: !s[d.deal_id] }))}
                        data-testid={`deal-row-${d.deal_id}`}
                      >
                        <TableCell>
                          <Link
                            to={`/deals/${d.deal_id}`}
                            className="text-blue-600 hover:underline text-sm font-medium"
                            onClick={e => e.stopPropagation()}
                            data-testid={`deal-link-${d.deal_id}`}
                          >
                            {d.deal_number}
                          </Link>
                        </TableCell>
                        <TableCell className="text-sm">{d.merchant || d.shortcode}{d.status && <div><Badge variant="outline" className="text-[10px] mt-0.5 font-normal text-gray-500">{d.status}</Badge></div>}</TableCell>
                        <TableCell className="text-sm text-gray-500 whitespace-nowrap">{d.funded_date || '-'}</TableCell>
                        <TableCell className="text-right text-sm">{formatCurrency(d.advance_amount)}</TableCell>
                        <TableCell className="text-right text-sm">{formatCurrency(d.target_collection)}</TableCell>
                        <TableCell className={`text-right text-sm font-medium ${overpaid ? 'text-red-600' : 'text-green-600'}`}>
                          {formatCurrency(d.running_collection)}
                        </TableCell>
                        <TableCell className="text-right text-sm text-orange-600">{formatCurrency(d.total_withdrawals)}</TableCell>
                        <TableCell className={`text-right text-sm font-semibold ${d.balance > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                          {formatCurrency(d.balance)}
                        </TableCell>
                        {isAdmin && (
                          <TableCell className="text-right">
                            <div>
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                value={withdrawAmounts[d.deal_id] || ''}
                                onChange={e => handleAmountChange(d.deal_id, e.target.value)}
                                className={`w-28 h-8 text-right text-sm ml-auto ${errMsg ? 'border-red-400 focus:ring-red-400' : ''}`}
                                placeholder="0.00"
                                data-testid={`withdraw-input-${d.deal_id}`}
                              />
                              {errMsg && <p className="text-[10px] text-red-500 mt-0.5 text-right" data-testid={`withdraw-error-${d.deal_id}`}>{errMsg}</p>}
                            </div>
                          </TableCell>
                        )}
                      </TableRow>,
                      isOpen && (
                        <TableRow key={`${d.deal_id}-perf`} className="bg-slate-50">
                          <TableCell colSpan={isAdmin ? 9 : 8} className="p-3">
                            <div className="text-xs font-medium text-slate-500 mb-2">This investor's performance on this deal</div>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                              <div><div className="text-xs text-gray-500">Capital deployed</div><div className="font-medium">{formatCurrency(d.advance_amount)}</div></div>
                              <div><div className="text-xs text-gray-500">Origination fee</div><div className="font-medium">{formatCurrency(d.fee_allocated)}</div></div>
                              <div><div className="text-xs text-gray-500">Collected</div><div className="font-medium text-green-600">{formatCurrency(p.collected)}</div></div>
                              <div><div className="text-xs text-gray-500">Fees collected</div><div className="font-medium text-green-600">{formatCurrency(p.fees_collected)}</div></div>
                              <div><div className="text-xs text-gray-500">Lawyer fees</div><div className="font-medium text-red-600">-{formatCurrency(p.lawyer_fees)}</div></div>
                              <div><div className="text-xs text-gray-500">Write-off loss</div><div className="font-medium text-red-600">-{formatCurrency(p.write_off_loss)}</div></div>
                              <div><div className="text-xs text-gray-500">Servicing</div><div className="font-medium text-red-600">-{formatCurrency(p.servicing)}</div></div>
                              <div><div className="text-xs text-gray-500">Net</div><div className="font-semibold">{formatCurrency(p.net)}</div></div>
                            </div>
                            {dealCategory(d.status) === 'DEFAULTED' && d.fees_detail && (
                              <div className="mt-3 border-t pt-2">
                                <div className="text-xs font-medium text-red-600 mb-1">Associated fees on this defaulted deal</div>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                                  <div><div className="text-xs text-gray-500">Default fee</div><div className="font-medium">{formatCurrency(d.fees_detail.default_fee)}</div></div>
                                  <div><div className="text-xs text-gray-500">Bounce / NSF</div><div className="font-medium">{formatCurrency(d.fees_detail.bounce_fee)}</div></div>
                                  <div><div className="text-xs text-gray-500">Lawyer / legal</div><div className="font-medium">{formatCurrency(d.fees_detail.lawyer_fee)}</div></div>
                                  <div><div className="text-xs text-gray-500">Write-off</div><div className="font-medium">{formatCurrency(d.fees_detail.write_off)}</div></div>
                                </div>
                              </div>
                            )}
                            <div className="mt-3">
                              <Link to={`/syndications/syndicator/${id}/deal/${d.deal_id}/report`} target="_blank" className="text-blue-600 hover:underline text-sm font-medium" data-testid={`gen-report-${d.deal_id}`}>Generate report →</Link>
                            </div>
                          </TableCell>
                        </TableRow>
                      )
                    ];
                  })}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Commission Credits — Phase 2A */}
        <CommissionCreditsSection syndicatorId={id} />
        </>}
      </div>
    </Layout>
  );
};

// ── Commission Credits Section ────────────────────────────────────────────
const CommissionCreditsSection = ({ syndicatorId }) => {
  const { getAuthHeaders } = useAuth();
  const [credits, setCredits] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!syndicatorId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(
          `${API}/transactions?syndicator_id=${syndicatorId}&type=COMMISSION_CREDIT`,
          { headers: getAuthHeaders() },
        );
        if (!cancelled) setCredits(Array.isArray(res.data) ? res.data : []);
      } catch {
        if (!cancelled) setCredits([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [syndicatorId, getAuthHeaders]);

  return (
    <Card data-testid="commission-credits-section" className="mt-6">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <DollarSign className="h-4 w-4 text-emerald-600" />
          Commission Credits ({credits.filter((c) => c.status !== 'void').length})
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <div className="py-6 text-center text-sm text-gray-500">Loading…</div>
        ) : credits.length === 0 ? (
          <div className="py-6 text-center text-sm text-gray-500">
            No commission credits. (Issued when a deal you funded gets its broker
            commission clawed back.)
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-600">
                <tr>
                  <th className="px-4 py-2 text-left">Date</th>
                  <th className="px-4 py-2 text-left">Deal</th>
                  <th className="px-4 py-2 text-left">Reference</th>
                  <th className="px-4 py-2 text-right">Amount</th>
                  <th className="px-4 py-2 text-left">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {credits.map((c) => {
                  const isVoid = c.status === 'void';
                  return (
                    <tr
                      key={c.id}
                      data-testid={`commission-credit-row-${c.id}`}
                      className={isVoid ? 'opacity-60' : ''}
                    >
                      <td className="px-4 py-2 font-mono text-xs">{c.date || c.transaction_date}</td>
                      <td className="px-4 py-2 font-mono text-xs">
                        <Link
                          to={`/deals/${c.deal_id}`}
                          className="text-blue-600 hover:underline"
                        >
                          #{c.deal_id_number || (c.deal_id || '').slice(0, 8)}
                        </Link>
                      </td>
                      <td className="px-4 py-2 font-mono text-xs text-gray-600">
                        {c.reference}
                      </td>
                      <td
                        className={`px-4 py-2 text-right tabular-nums font-semibold ${
                          isVoid ? 'line-through text-gray-500' : 'text-emerald-700'
                        }`}
                      >
                        {formatCurrency(c.amount)}
                      </td>
                      <td className="px-4 py-2 text-xs">
                        {isVoid ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded bg-gray-200 text-gray-700">
                            Void {c.metadata?.reverse_reason ? `· ${c.metadata.reverse_reason}` : ''}
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 rounded bg-emerald-100 text-emerald-700">
                            Active
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SyndicatorDetail;
